import type { InboxEvent, InboxEventKind } from "@foreman/protocol";
import type { InboxItem } from "./hub";

/** What to show the user. Plain data, so any platform (web notifications, native) can render it. */
export interface Notice {
  title: string;
  body: string;
  /** Stable per event, so a notification can be replaced or cleared when the event is resolved. */
  tag: string;
  computerId: string;
  eventId: string;
  /** The session the event came from, unless the agent runs outside Brocoded. */
  sessionId?: string;
  kind: InboxEventKind;
}

export interface InboxFeed {
  subscribe(cb: () => void): () => void;
  getInbox(): InboxItem[];
}

export interface NotifierOptions {
  notify(notice: Notice): void;
  /** Close a notification that no longer applies (the event was resolved or removed). */
  clear?(tag: string): void;
  /** Called for each new event; return false to stay quiet (for example while the app is in front). */
  shouldNotify?(): boolean;
  /** Called with each new event; return false to stay quiet about this one (alert choices, quiet hours). */
  allow?(event: InboxEvent): boolean;
  kinds?: InboxEventKind[];
  /** More than one computer: then the alert names the computer too, otherwise it is just noise. */
  multi?: () => boolean;
  now?: () => number;
}

const TITLES: Record<InboxEventKind, string> = {
  approval: "needs approval",
  question: "has a question",
  completed: "finished",
  failed: "hit an error",
};

export function noticeFor(item: InboxItem, multi = false): Notice {
  const { computer, event } = item;
  const asked = event.kind === "question" ? event.questions?.[0]?.question : undefined;
  const text = asked || (event.detail ? `${event.title}: ${event.detail}` : event.title);
  const body = multi ? `${computer.label} · ${text}` : text;
  return {
    title: `${event.agent === "claude-code" ? "Claude" : event.agent} ${TITLES[event.kind]}`,
    body: body.length > 140 ? body.slice(0, 139) + "…" : body,
    tag: `foreman-${event.id}`,
    computerId: computer.id,
    eventId: event.id,
    ...(!event.external && { sessionId: event.sessionId }),
    kind: event.kind,
  };
}

/**
 * Turns the inbox feed into notifications. Deterministic rules, no scoring:
 *  - only events that appear after the notifier started notify (opening the app does not replay old items);
 *  - each event notifies at most once, even across reconnects;
 *  - events that are resolved or removed clear their notification.
 */
export class Notifier {
  private readonly seen = new Set<string>();
  private readonly shown = new Map<string, string>(); // eventId -> tag
  private readonly startedAt: number;
  private readonly kinds: InboxEventKind[];
  private off: (() => void) | null = null;

  constructor(
    private readonly feed: InboxFeed,
    private readonly opts: NotifierOptions,
  ) {
    this.startedAt = (opts.now ?? Date.now)();
    this.kinds = opts.kinds ?? ["approval", "question", "completed", "failed"];
  }

  start(): () => void {
    for (const item of this.feed.getInbox()) this.seen.add(item.event.id); // what is already there is not news
    this.off = this.feed.subscribe(() => this.check());
    return () => this.stop();
  }

  stop(): void {
    this.off?.();
    this.off = null;
  }

  private check(): void {
    const items = this.feed.getInbox();
    const present = new Set<string>();
    for (const item of items) {
      const e = item.event;
      present.add(e.id);
      if (e.state === "resolved") {
        this.clear(e.id);
        continue;
      }
      if (this.seen.has(e.id)) continue;
      this.seen.add(e.id);
      if (e.createdAt < this.startedAt || !this.kinds.includes(e.kind)) continue;
      if (this.opts.shouldNotify && !this.opts.shouldNotify()) continue;
      if (this.opts.allow && !this.opts.allow(e)) continue;
      const notice = noticeFor(item, this.opts.multi?.() ?? false);
      this.shown.set(e.id, notice.tag);
      this.opts.notify(notice);
    }
    for (const id of [...this.shown.keys()]) if (!present.has(id)) this.clear(id);
  }

  private clear(eventId: string): void {
    const tag = this.shown.get(eventId);
    if (tag === undefined) return;
    this.shown.delete(eventId);
    this.opts.clear?.(tag);
  }
}

export type { InboxEvent };
